import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@carrent/ui'
import { regionsApi, regionsAdminApi } from '@carrent/api'

export default function AdminRegionsPage() {
  const navigate = useNavigate()
  const [regions, setRegions] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingRegion, setEditingRegion] = useState(null)

  const load = () => {
    setLoading(true)
    regionsAdminApi.list()
      .then(r => setRegions(r.data?.data || []))
      .catch(() => {})
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const handleDelete = async (region) => {
    const total = (region.counts?.cars || 0) + (region.counts?.drivers || 0)
                + (region.counts?.managers || 0)
    if (total > 0) {
      alert(`Нельзя удалить — в регионе ${total} объектов`)
      return
    }
    if (!confirm(`Удалить регион «${region.name}»?`)) return
    try {
      await regionsAdminApi.remove(region.id)
      load()
    } catch (e) {
      alert(e.response?.data?.message || 'Ошибка')
    }
  }

  return (
    <div style={{ paddingBottom: 'var(--gap-xl)' }}>
      <PageHeader title="Регионы"
        right={
          <button className="btn btn--primary"
            style={{ padding: '6px 14px', fontSize: '0.85rem' }}
            onClick={() => { setEditingRegion(null); setShowForm(true) }}>
            + Добавить
          </button>
        }
      />

      <div className="p-page flex-col" style={{ gap: 'var(--gap-sm)' }}>
        <p className="text-muted" style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>
          Регионы изолируют машины и водителей. Менеджеры и водители видят только свой регион,
          администратор без региона видит всё.
        </p>

        {loading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
            <div className="spinner" />
          </div>
        )}

        {!loading && regions.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: 12 }}>🌍</div>
            <p>Нет регионов</p>
          </div>
        )}

        {regions.map(r => (
          <div key={r.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{
              padding: '12px var(--gap)',
              borderBottom: '1px solid var(--border)',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
              gap: 8, cursor: 'pointer',
            }}
            onClick={(e) => {
              if (e.target.closest('button')) return
              navigate(`/admin/regions/${r.id}`)
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontFamily: 'monospace', background: 'var(--accent)',
                    color: '#fff', padding: '2px 8px', borderRadius: 4,
                    fontSize: '0.75rem', fontWeight: 700,
                  }}>{r.code}</span>
                  <p style={{ fontWeight: 700, fontSize: '0.95rem' }}>{r.name}</p>
                  {!r.is_active && (
                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)',
                      padding: '1px 6px', background: 'var(--bg)', borderRadius: 4 }}>
                      Не активен
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>›</span>
                </div>
                <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: 4 }}>
                  {r.timezone}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingRegion(r); setShowForm(true) }}
                  style={{ background: 'none', border: '1px solid var(--border)',
                    borderRadius: 'var(--r-sm)', padding: '4px 10px',
                    cursor: 'pointer', fontSize: '0.78rem' }}>
                  ✏️
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(r) }}
                  style={{ background: 'none', border: '1px solid rgba(239,68,68,0.3)',
                    borderRadius: 'var(--r-sm)', padding: '4px 10px',
                    cursor: 'pointer', fontSize: '0.78rem', color: 'var(--danger)' }}>
                  🗑
                </button>
              </div>
            </div>

            {/* Счётчики (тоже кликабельны и ведут на детальную) */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
              padding: '8px 0', cursor: 'pointer' }}
              onClick={() => navigate(`/admin/regions/${r.id}`)}>
              <div style={{ textAlign: 'center', padding: '4px',
                borderRight: '1px solid var(--border)' }}>
                <p style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                  {r.counts?.cars || 0}
                </p>
                <p className="text-muted" style={{ fontSize: '0.7rem' }}>🚗 машин</p>
              </div>
              <div style={{ textAlign: 'center', padding: '4px',
                borderRight: '1px solid var(--border)' }}>
                <p style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                  {r.counts?.drivers || 0}
                </p>
                <p className="text-muted" style={{ fontSize: '0.7rem' }}>🚙 водителей</p>
              </div>
              <div style={{ textAlign: 'center', padding: '4px' }}>
                <p style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                  {r.counts?.managers || 0}
                </p>
                <p className="text-muted" style={{ fontSize: '0.7rem' }}>🔧 менеджеров</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <RegionForm
          region={editingRegion}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load() }}
        />
      )}
    </div>
  )
}

function RegionForm({ region, onClose, onSaved }) {
  const [name, setName]         = useState(region?.name || '')
  const [code, setCode]         = useState(region?.code || '')
  const [tz, setTz]             = useState(region?.timezone || 'Europe/Moscow')
  const [isActive, setIsActive] = useState(region?.is_active ?? true)
  const [saving, setSaving]     = useState(false)
  const [err, setErr]           = useState('')

  const submit = async () => {
    setErr(''); setSaving(true)
    try {
      const payload = { name, code, timezone: tz, is_active: isActive }
      if (region) {
        await regionsAdminApi.update(region.id, payload)
      } else {
        await regionsAdminApi.create(payload)
      }
      onSaved()
    } catch (e) {
      setErr(e.response?.data?.message || 'Ошибка сохранения')
    } finally { setSaving(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)',
      display: 'flex', alignItems: 'flex-end', zIndex: 300 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: '100%', maxWidth: 480, margin: '0 auto',
        background: 'var(--bg-card)',
        borderRadius: 'var(--r-xl) var(--r-xl) 0 0',
        padding: 'var(--gap)',
        paddingBottom: 'calc(var(--gap) + env(safe-area-inset-bottom, 0px))',
        maxHeight: '88vh', overflowY: 'auto',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 'var(--gap)' }}>
          <h3 style={{ margin: 0 }}>
            {region ? 'Редактировать регион' : 'Новый регион'}
          </h3>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-muted)',
              fontSize: '1.5rem', cursor: 'pointer' }}>✕</button>
        </div>

        <div className="flex-col" style={{ gap: 'var(--gap-sm)' }}>
          <div className="field" style={{ margin: 0 }}>
            <label className="field__label">Название *</label>
            <input className="input" value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Москва" />
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="field__label">Код *</label>
            <input className="input" value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              maxLength={10}
              style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}
              placeholder="MSK" />
            <p className="text-muted" style={{ fontSize: '0.72rem', marginTop: 4 }}>
              Короткий код 2–10 символов (MSK, SPB, KAZ)
            </p>
          </div>

          <div className="field" style={{ margin: 0 }}>
            <label className="field__label">Часовой пояс</label>
            <select className="input" value={tz} onChange={e => setTz(e.target.value)}>
              <option>Europe/Moscow</option>
              <option>Europe/Kaliningrad</option>
              <option>Europe/Samara</option>
              <option>Asia/Yekaterinburg</option>
              <option>Asia/Omsk</option>
              <option>Asia/Krasnoyarsk</option>
              <option>Asia/Irkutsk</option>
              <option>Asia/Vladivostok</option>
            </select>
          </div>

          {region && (
            <button type="button"
              onClick={() => setIsActive(v => !v)}
              style={{
                padding: 'var(--gap-sm)',
                border: `2px solid ${isActive ? 'var(--success)' : 'var(--border)'}`,
                borderRadius: 'var(--r)',
                background: isActive ? 'rgba(34,197,94,0.08)' : 'var(--bg)',
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10,
              }}>
              <div style={{
                width: 22, height: 22, borderRadius: 6,
                border: `2px solid ${isActive ? 'var(--success)' : 'var(--border)'}`,
                background: isActive ? 'var(--success)' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isActive && <span style={{ color: '#fff', fontSize: '0.85rem' }}>✓</span>}
              </div>
              <span style={{ fontSize: '0.88rem',
                color: isActive ? 'var(--success)' : 'var(--text-muted)' }}>
                Регион активен
              </span>
            </button>
          )}

          {err && <div className="alert alert--error">{err}</div>}

          <button className="btn btn--primary btn--full"
            disabled={saving || !name.trim() || !code.trim()}
            onClick={submit}>
            {saving ? <span className="spinner" /> : (region ? 'Сохранить' : 'Создать')}
          </button>
        </div>
      </div>
    </div>
  )
}
